import { DataLayerECommerce, DataLayerEvent, getWindow, SignInMethod, WindowAccess } from "../types/window";
import { Cart } from "../types/dto";
import { ProductPrice } from "../types/graphQLTypes";
import { Currency } from "@on/model-store";
import { toDataLayerItems } from "@on/model-store/src/dataLayer";
import { cartLineChange, hashUserId, isItemOf, itemsValue, LoyaltyLevel, loyaltyLevel } from "./dataLayerHelper";

interface SingleItem {
  name: string;
  email?: string;
  baselinker_id: string;
  prices: Record<Currency, ProductPrice>;
}

/** Tracking must never break the cart or checkout flow it is called from. */
const safely =
  <A extends unknown[]>(push: (...args: A) => void) =>
  (...args: A) => {
    try {
      push(...args);
    } catch (error) {
      console.error("dataLayer push failed", error);
    }
  };

export const singleItemPush = safely((item: SingleItem) => {
  const price = item.prices["PLN"];
  const now = Date.now();
  const sale = Boolean(price.sale_price_start && price.sale_price_end && now > price.sale_price_start && now < price.sale_price_end && price.sale_price);
  const discountValue = sale ? (price.non_sale_price - (price.sale_price ?? 0)).toFixed(2) : 0;
  const itemPrice = sale ? price.sale_price ?? price.non_sale_price : price.non_sale_price;

  const windowAccess = getWindow();

  windowAccess.dataLayer = windowAccess.dataLayer || [];
  windowAccess.dataLayer.push({ ecommerce: null });
  windowAccess.dataLayer.push({
    event: `view_item`,
    user: item.email,
    //TODO
    currency: "PLN",
    value: Number((itemPrice / 1.23).toFixed(2)),
    ecommerce: {
      items: [
        {
          item_name: item.name,
          item_id: item.baselinker_id,
          currency: "PLN",
          price: Number(itemPrice.toFixed(2)),
          discount: Number(discountValue),
          quantity: 1,
        },
      ],
    },
  });
});

export const viewItemListPush = safely(
  (
    listItems: {
      name: string;
      baselinker_id: string;
      prices: {
        PLN: {
          sale_price?: number;
          sale_price_start?: number; // timestamp
          sale_price_end?: number; // timestamp
          non_sale_price: number;
        };
      };
    }[],
    category: string
  ) => {
    const now = Date.now();

    const items: DataLayerECommerce["items"] = listItems.map((i) => {
      const sale = Boolean(
        i.prices["PLN"].sale_price_start && i.prices["PLN"].sale_price_end && now > i.prices["PLN"].sale_price_start && now < i.prices["PLN"].sale_price_end && i.prices["PLN"].sale_price
      );
      return {
        item_name: i.name,
        item_id: i.baselinker_id,
        currency: "PLN",
        price: sale ? i.prices["PLN"].sale_price ?? i.prices["PLN"].non_sale_price : i.prices["PLN"].non_sale_price,
        discount: sale ? Number((i.prices["PLN"].non_sale_price - (i.prices["PLN"].sale_price ?? i.prices["PLN"].non_sale_price)).toFixed(2)) : 0,
        quantity: 1,
      };
    });
    const windowAccess = getWindow();
    windowAccess.dataLayer = windowAccess.dataLayer || [];
    windowAccess.dataLayer.push({ ecommerce: null });
    windowAccess.dataLayer.push({
      event: `view_item_list`,
      currency: "PLN",
      item_list_name: `${category}`,
      ecommerce: {
        items,
      },
    });
  }
);

export const cartItemsPush = safely((state: Cart, event: DataLayerEvent, eventCallback?: (id: string) => void, params?: { shipping_tier?: string; payment_type?: string }) => {
  const email = state?.email || undefined;
  const items: DataLayerECommerce["items"] = toDataLayerItems(state);
  const windowAccess = getWindow();
  windowAccess.dataLayer = windowAccess.dataLayer || [];
  windowAccess.dataLayer.push({ ecommerce: null });
  const totalItems = items.reduce((acc, item) => acc + item.quantity, 0);
  windowAccess.dataLayer.push({
    event: `${event}`,
    ...(["begin_checkout", "purchase"].includes(event) && process.env.GATSBY_EXPERIMENT_NAME && process.env.GATSBY_EXPERIMENT_VARIANT
      ? { experiment_name: process.env.GATSBY_EXPERIMENT_NAME, experiment_variant: process.env.GATSBY_EXPERIMENT_VARIANT }
      : undefined),
    ...(eventCallback ? { eventCallback } : undefined),
    ...params,
    value: itemsValue(items),
    coupon: state?.coupon_code || undefined,
    ecommerce: {
      items,
    },
    currency: "PLN",
    ...(event === "purchase" ? { transaction_id: state?.metadata?.transactionId as string } : undefined),
    ...(event === "purchase" ? { shipping: Number(state.shipment_total.toFixed(2)) } : undefined),
    user: email,
    tax: state?.tax_total.toFixed(2),
    num_items: totalItems,
  });
});

/** A bundle's components are one each per bundle, so every pushed item gets the quantity added. */
export const cartSingleItemPush = safely((cart: Cart, productId: string, quantity: number) => {
  const item = cart.items.find((item) => item.product.product_id === productId);
  if (!item) {
    console.error(`No item found in cart with productId ${productId}, cart: ${JSON.stringify(cart)}`);
    return;
  }
  const items: DataLayerECommerce["items"] = toDataLayerItems(cart)
    .filter((i) => isItemOf(i, productId))
    .map((i) => ({ ...i, quantity }));

  const email = cart?.email || undefined;
  const windowAccess = getWindow();
  windowAccess.dataLayer = windowAccess.dataLayer || [];
  windowAccess.dataLayer.push({ ecommerce: null });
  windowAccess.dataLayer.push({
    event: `add_to_cart`,
    value: itemsValue(items),
    currency: "PLN",
    user: email,
    transaction_id: cart.cartId + "#" + String(cart.version),
    ecommerce: {
      items,
    },
  });
});

/** add_to_cart / remove_from_cart for what changed in one product's line (+, −, removing it). */
export const cartChangePush = safely((before: Cart, after: Cart, productId: string) => {
  const { added, removed } = cartLineChange(toDataLayerItems(before), toDataLayerItems(after), productId);
  const windowAccess = getWindow();
  windowAccess.dataLayer = windowAccess.dataLayer || [];
  const push = (event: "add_to_cart" | "remove_from_cart", items: DataLayerECommerce["items"]) => {
    if (!items.length) {
      return;
    }
    windowAccess.dataLayer.push({ ecommerce: null });
    windowAccess.dataLayer.push({
      event,
      value: itemsValue(items),
      currency: "PLN",
      user: after?.email || undefined,
      ...(event === "add_to_cart" ? { transaction_id: after.cartId + "#" + String(after.version) } : undefined),
      ecommerce: {
        items,
      },
    });
  };
  push("add_to_cart", added);
  push("remove_from_cart", removed);
});

const push = (entry: WindowAccess["dataLayer"][number]) => {
  const windowAccess = getWindow();
  windowAccess.dataLayer = windowAccess.dataLayer || [];
  windowAccess.dataLayer.push(entry);
};

export const joinLoyaltyPush = safely(() => push({ event: "join_group", group_id: "moja_opcja_natura" }));

/** the order's purchase, and the points it spends: points are only taken from the account when the order is placed */
export const purchasePush = (cart: Cart) => {
  cartItemsPush(cart, "purchase");
  const points = cart.loyaltyPointsUsed ?? 0;
  if (points > 0) {
    safely(() => push({ event: "spend_virtual_currency", virtual_currency_name: "punkty MON", value: points }))();
  }
};

type TrackedAccount = { accountId?: string; loyaltyLevel?: LoyaltyLevel };

export const trackedAccount = (cart: Pick<Cart, "account_logged_in" | "account_id" | "account">): TrackedAccount =>
  cart.account_logged_in && cart.account_id ? { accountId: cart.account_id, loyaltyLevel: loyaltyLevel(cart.account) } : {};

let userDataSeq = 0;

/**
 * Who is browsing, for GTM to set GA4 `user_id` and the `loyalty_level` user property. A guest sends nulls, which clears both
 * after a logout. Only the latest call pushes: hashing is async, so an older state could otherwise land after a newer one.
 */
/* crypto.subtle exists only on https (and localhost): without it the level still goes, without an id */
const userDataEntry = async ({ accountId, loyaltyLevel: level }: TrackedAccount) => ({
  event: "user_data" as const,
  user_id: accountId ? await hashUserId(accountId).catch(() => null) : null,
  loyalty_level: accountId ? level ?? null : null,
});

export const userDataPush = async (account: TrackedAccount) => {
  const seq = ++userDataSeq;
  const entry = await userDataEntry(account);
  if (seq === userDataSeq) {
    safely(() => push(entry))();
  }
};

/** its own `user_data` first, so the login / sign_up hit already carries the account's `user_id` (the layout's push comes after it) */
export const signInPush = async (event: "login" | "sign_up", method: SignInMethod, cart?: Parameters<typeof trackedAccount>[0]) => {
  const entry = cart ? await userDataEntry(trackedAccount(cart)) : undefined;
  safely(() => {
    if (entry) {
      push(entry);
    }
    push({ event, method });
  })();
};
